import { useEventParticipantStore } from "@/store/EventParticipant";
import { createApp, App as VueApp } from "vue";
import { onAuthStateChanged, User } from "firebase/auth";
import { createPinia } from "pinia";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { auth, remoteConfig } from "@/firebase";
import * as Sentry from "@sentry/vue";
import { useUserProfileStore } from "@/store/UserProfile";
import { createPersistedState } from "pinia-plugin-persistedstate";
import Config from "@/config/Config";
import App from "./App.vue";
import router from "./router";
import "bottom-navigation-vue/dist/style.css";
import "./assets/styles/main.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import 'vue3-datepicker/dist/vue3-datepicker.css';

let app: VueApp;

async function initializeApp(user: User | null) {
  const pinia = createPinia();
  pinia.use(createPersistedState());

  app = createApp(App);
  
  // Automatically register all App* components
  const components = import.meta.glob('@/components/App*.vue', { eager: true });
  Object.entries(components).forEach(([path, module]) => {
    // Get component name from path (e.g., '@/components/AppButton.vue' -> 'AppButton')
    const componentName = path.split('/').pop()?.replace('.vue', '') || '';
    app.component(componentName, (module as any).default);
  });

  app.use(router);
  app.use(pinia);
  app.component("font-awesome-icon", FontAwesomeIcon);
  app.provide("remoteConfig", remoteConfig);

  // Sentry configuration
  Sentry.init({
    app,
    dsn: Config.sentryIngestUrl,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: Config.sentryTargets,
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      new Sentry.Replay(),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  
  if (user) {
    const userProfileStore = useUserProfileStore();
    await userProfileStore.setLoginProfile(user);
    await userProfileStore.syncProfileWithDb()
  }

  app.mount("#app");

  const eventParticipantStore = useEventParticipantStore();
  eventParticipantStore.$subscribe(() => {
    const dataToCache = eventParticipantStore.getData;
    localStorage.setItem("cachedeventParticipantData", JSON.stringify(dataToCache));
  });
}

Sentry.setTag("buildNumber", Config.buildNumber);

onAuthStateChanged(
  auth,
  async (user: User | null) => {
    if (!app) {
      await initializeApp(user);
    }
  },
  (error: Error) => {
    console.error(`Main AuthStateChange handler failed with error ${error}`);
  }
);
